import { IReduxState } from "#webapp/src/state/reducers/root.reducer";
import { Button, ButtonSet, Select, SelectOption, TextField } from "@clockwise/design-system";
import { SidebarDialog } from "@clockwise/web-commons/src/components/sidebar-dialog";
import { DialogActions, DialogContent } from "@material-ui/core";
import { CSSProperties } from "@material-ui/core/styles/withStyles";
import * as React from "react";
import { useSelector } from "react-redux";

type Props = {
  defaultDuration: number;
  maxDuration: number;
  durations: number[];
  onDurationChange: (minutesDuration: number) => void;
  disabled?: boolean;
  menuStyle?: CSSProperties;
  showLabel?: boolean;
  "cw-id"?: string;
};

export const DurationPicker = ({
  defaultDuration,
  durations,
  maxDuration,
  disabled,
  showLabel,
  onDurationChange,
  "cw-id": cwId,
}: Props) => {
  const isChromeExtension = useSelector((state: IReduxState) => state.webExtension.isWebExtension);
  const [duration, setDuration] = React.useState(defaultDuration);
  const [inputDialogOpen, setInputDialogOpen] = React.useState(false);
  const [inputDialogValue, setInputDialogValue] = React.useState("");
  const [inputDialogError, setInputDialogError] = React.useState("");

  if (!durations.includes(duration)) {
    durations.unshift(duration);
  }

  const onSelectChange = (duration: number) => {
    handleDurationChange(duration);
  };

  const handleDurationChange = (updatedDuration: number, setStateCallback?: () => void) => {
    if (updatedDuration < 0) {
      return openInputDialog(true);
    }

    onDurationChange(updatedDuration);
    const newInputDialogValue = updatedDuration === duration ? inputDialogValue : "";
    setDuration(updatedDuration);
    setInputDialogValue(newInputDialogValue);
    setStateCallback?.();
  };

  const openInputDialog = (updatedInputDialogOpen: boolean) => {
    setInputDialogOpen(updatedInputDialogOpen);
  };

  const onInputDialogChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedInputDialogValue = event.target.value;
    setInputDialogValue(updatedInputDialogValue);
    setInputDialogError("");
  };

  const onInputDialogSubmit = () => {
    const updatedDuration = Math.round(Number(inputDialogValue));

    if (!updatedDuration) {
      setInputDialogError("Please enter a valid duration");
      return;
    }

    if (updatedDuration > maxDuration) {
      setInputDialogError(`A duration over ${maxDuration} minutes isn't supported`);
      return;
    } else if (updatedDuration < 1) {
      setInputDialogError(`A duration under 1 minutes isn't supported`);
      return;
    }

    handleDurationChange(updatedDuration, () => {
      openInputDialog(false);
    });
  };

  return (
    <div className="cw-flex cw-items-center">
      <Select
        label={showLabel ? "Duration" : ""}
        value={duration}
        onChange={onSelectChange}
        name="Duration"
        disabled={!!disabled}
        cw-id={cwId}
      >
        {durations.map((d) => (
          <SelectOption key={d} value={d}>
            {d} min
          </SelectOption>
        ))}
        <SelectOption value={-1}>Custom</SelectOption>
      </Select>
      {inputDialogOpen && (
        <SidebarDialog
          isChromeExtension={isChromeExtension}
          open={inputDialogOpen}
          onClose={() => openInputDialog(false)}
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              onInputDialogSubmit();
            }
          }}
          role="dialog"
        >
          <DialogContent>
            <div className="cw-heading-lg cw-mb-2">Meeting Duration</div>
            <div className="cw-flex cw-w-full cw-items-center">
              <TextField
                autoFocus
                type="number"
                min={1}
                max={maxDuration}
                value={inputDialogValue}
                onChange={onInputDialogChange}
                error={!!inputDialogError}
                errorMessage={inputDialogError}
                step={5}
              />
              <div style={{ marginLeft: 10 }}>minutes</div>
            </div>
          </DialogContent>
          <DialogActions>
            <ButtonSet>
              <Button variant="text" onClick={onInputDialogSubmit} sentiment="positive">
                Confirm
              </Button>
              <Button
                variant="text"
                onClick={() => {
                  setInputDialogValue("");
                  setInputDialogError("");
                  openInputDialog(false);
                }}
              >
                Cancel
              </Button>
            </ButtonSet>
          </DialogActions>
        </SidebarDialog>
      )}
    </div>
  );
};
